import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MessageCircle, Phone, Check, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Layout from '@/components/layout/Layout';
import BookingModal from '@/components/BookingModal';
import JsonLd, { generateServiceSchema, generateBreadcrumbSchema } from '@/components/seo/JsonLd';
import FAQSection from '@/components/FAQSection';
import SEOHead from '@/components/seo/SEOHead';
import PageBreadcrumb from '@/components/PageBreadcrumb';
import OptimizedImage from '@/components/ui/OptimizedImage';
import iphoneImage from '@/assets/iphone-repair.jpg';

const iPhoneServiceSchema = generateServiceSchema({
  name: 'iPhone Repair Service in Patna, Bihar',
  description: 'Expert iPhone repair services in Bihar. Screen replacement, battery replacement, logic board repair for all iPhone models from iPhone 6 to iPhone 16 Pro Max.',
  url: 'https://ifixappleservicecenter.in/iphone-service',
  ratingValue: 4.9,
  reviewCount: 500,
});

const breadcrumbSchema = generateBreadcrumbSchema([
  { name: 'Home', url: 'https://ifixappleservicecenter.in/' },
  { name: 'iPhone Service', url: 'https://ifixappleservicecenter.in/iphone-service' },
]);

const iphoneFaqs = [
  {
    question: 'How long does iPhone screen replacement take in Patna?',
    answer: 'Most iPhone screen replacements are completed within 30-60 minutes at our Patna service center. For complex repairs involving additional components, it may take 2-3 hours.',
  },
  {
    question: 'What is the cost of iPhone battery replacement in Bihar?',
    answer: 'iPhone battery replacement costs vary by model. Contact us at +91 97986 53591 for an exact quote for your specific model. All replacements include a 6-month warranty.',
  },
  {
    question: 'Do you use original Apple parts for iPhone repairs?',
    answer: 'We use high-quality OEM-grade parts that match Apple specifications. All our replacement parts come with a warranty and undergo quality testing before installation.',
  },
  {
    question: 'Can you fix water-damaged iPhones?',
    answer: 'Yes, we specialize in water damage recovery for all iPhone models. Success rates depend on the extent of damage and how quickly you bring the device. We recommend not charging or turning on a water-damaged iPhone before professional inspection.',
  },
  {
    question: 'Is there a warranty on iPhone repairs?',
    answer: 'All our iPhone repairs come with a 3-month warranty covering parts and labor. Screen replacements and battery replacements are backed by our quality guarantee.',
  },
];

interface ServiceItem {
  name: string;
  path?: string;
}

const IPhoneService = () => {
  const [isBookingOpen, setIsBookingOpen] = useState(false);

  const services: ServiceItem[] = [
    { name: 'Screen Replacement', path: '/iphone-service/screen-replacement' },
    { name: 'Battery Replacement', path: '/iphone-service/battery-replacement' },
    { name: 'Logic Board Repair' },
    { name: 'Back Glass Replacement' },
    { name: 'Water Damage Repair' },
    { name: 'Overheating Issue Fix' },
    { name: 'Network Issue' },
    { name: 'Stuck on Logo' },
    { name: 'Charging Kit Replacement' },
    { name: 'Storage Upgrade' },
    { name: 'Touch Not Working' },
    { name: 'Earpiece Replacement' },
    { name: 'Speaker Replacement' },
    { name: 'Face ID Issue' },
  ];

  const models = [
    'iPhone 6', 'iPhone 6 Plus', 'iPhone 6s', 'iPhone 6s Plus',
    'iPhone 7', 'iPhone 7 Plus', 'iPhone 8', 'iPhone 8 Plus',
    'iPhone X', 'iPhone XR', 'iPhone XS', 'iPhone XS Max',
    'iPhone 11', 'iPhone 11 Pro', 'iPhone 11 Pro Max',
    'iPhone 12', 'iPhone 12 Mini', 'iPhone 12 Pro', 'iPhone 12 Pro Max',
    'iPhone 13', 'iPhone 13 Mini', 'iPhone 13 Pro', 'iPhone 13 Pro Max',
    'iPhone 14', 'iPhone 14 Plus', 'iPhone 14 Pro', 'iPhone 14 Pro Max',
    'iPhone 15', 'iPhone 15 Plus', 'iPhone 15 Pro', 'iPhone 15 Pro Max',
    'iPhone 16', 'iPhone 16 Plus', 'iPhone 16 Pro', 'iPhone 16 Pro Max',
    'iPhone 17 Pro Max',
  ];

  return (
    <Layout>
      <SEOHead
        title="iPhone Service Center Patna Bihar | iPhone Repair & Battery Replacement Near Me - iFix"
        description="Best iPhone service center in Patna Bihar. Expert iPhone repair, iPhone screen replacement, iPhone battery replacement & iPhone back glass replacement. Apple iPhone service center near me with same-day service. Book now!"
        url="https://ifixappleservicecenter.in/iphone-service"
      />
      <JsonLd data={iPhoneServiceSchema} />
      <JsonLd data={breadcrumbSchema} />
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <OptimizedImage
            src={iphoneImage}
            alt="iPhone repair service center in Patna Bihar - screen replacement battery fix logic board repair"
            className="w-full h-full object-cover"
            priority
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/90 to-background/60" />
        </div>
        
        <div className="container-apple relative z-10 py-20">
          <PageBreadcrumb 
            items={[{ name: 'iPhone Service', path: '/iphone-service' }]} 
            className="mb-8"
          />
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-foreground leading-tight mb-6 opacity-0 animate-fade-in">
            iPhone Service Center
            <span className="block text-primary">in Patna, Bihar</span>
          </h1>
          
          <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl leading-relaxed opacity-0 animate-fade-in animation-delay-100">
            Looking for an iPhone service center near me? iFix is your trusted Apple iPhone service center in Bihar offering expert iPhone repair, 
            iPhone screen replacement, and iPhone battery replacement services with genuine parts and same-day service.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 opacity-0 animate-fade-in animation-delay-200">
            <Button variant="apple-blue" size="lg" onClick={() => setIsBookingOpen(true)}>
              <Calendar className="w-5 h-5" />
              Book iPhone Repair
            </Button>
            <Button variant="whatsapp" size="lg" asChild>
              <a
                href="https://wa.me/919798653591?text=Hi,%20I%20need%20help%20with%20my%20iPhone"
                target="_blank"
                rel="noopener noreferrer"
              >
                <MessageCircle className="w-5 h-5" />
                WhatsApp
              </a>
            </Button>
            <Button variant="call" size="lg" asChild>
              <a href="tel:9798653591">
                <Phone className="w-5 h-5" />
                Call Now
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-padding">
        <div className="container-apple">
          <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-4 text-center">
            iPhone Repair Services
          </h2>
          <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
            Our iPhone service center in Patna offers comprehensive iPhone repair solutions including iPhone screen replacement, iPhone battery replacement, and iPhone back glass replacement. Same day service available at our i phone service centre.
          </p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {services.map((service, index) => {
              const CardContent = (
                <>
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4 text-primary" />
                  </div>
                  <span className="font-medium text-foreground flex-1">{service.name}</span>
                  {service.path && <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                </>
              );

              if (service.path) {
                return (
                  <Link
                    key={service.name}
                    to={service.path}
                    className="flex items-center gap-3 p-4 bg-card rounded-2xl border border-border hover:border-primary/30 hover:shadow-apple-sm transition-all opacity-0 animate-fade-in group"
                    style={{ animationDelay: `${index * 50}ms`, animationFillMode: 'forwards' }}
                  >
                    {CardContent}
                  </Link>
                );
              }

              return (
                <div
                  key={service.name}
                  className="flex items-center gap-3 p-4 bg-card rounded-2xl border border-border hover:border-primary/30 hover:shadow-apple-sm transition-all opacity-0 animate-fade-in"
                  style={{ animationDelay: `${index * 50}ms`, animationFillMode: 'forwards' }}
                >
                  {CardContent}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Supported Models */}
      <section className="section-padding bg-secondary">
        <div className="container-apple">
          <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-4 text-center">
            Supported iPhone Models
          </h2>
          <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
            We repair all iPhone models from iPhone 6 to the latest iPhone 17 Pro Max.
          </p>
          
          <div className="flex flex-wrap justify-center gap-3">
            {models.map((model) => (
              <span
                key={model}
                className="px-4 py-2 bg-background rounded-full text-sm font-medium text-foreground border border-border"
              >
                {model}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Related Content */}
      <section className="section-padding">
        <div className="container-apple">
          <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-8 text-center">
            Related Resources
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            <Link
              to="/blog/iphone-battery-draining-fast-fix-patna"
              className="p-6 bg-card rounded-2xl border border-border hover:border-primary/30 hover:shadow-apple-sm transition-all group"
            >
              <h3 className="font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                iPhone Battery Drain Fix
              </h3>
              <p className="text-sm text-muted-foreground">
                Complete guide to fix iPhone battery issues.
              </p>
            </Link>
            <Link
              to="/blog/iphone-not-charging-solutions-bihar"
              className="p-6 bg-card rounded-2xl border border-border hover:border-primary/30 hover:shadow-apple-sm transition-all group"
            >
              <h3 className="font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                iPhone Not Charging
              </h3>
              <p className="text-sm text-muted-foreground">
                10 solutions for charging problems.
              </p>
            </Link>
            <Link
              to="/blog/iphone-water-damage-recovery-patna"
              className="p-6 bg-card rounded-2xl border border-border hover:border-primary/30 hover:shadow-apple-sm transition-all group"
            >
              <h3 className="font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                Water Damage Recovery
              </h3>
              <p className="text-sm text-muted-foreground">
                Emergency water damage repair guide.
              </p>
            </Link>
            <Link
              to="/blog/iphone-overheating-causes-fix-patna"
              className="p-6 bg-card rounded-2xl border border-border hover:border-primary/30 hover:shadow-apple-sm transition-all group"
            >
              <h3 className="font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                iPhone Overheating Fix
              </h3>
              <p className="text-sm text-muted-foreground">
                Causes and solutions for overheating.
              </p>
            </Link>
          </div>
          
          {/* Cross-linking to other services */}
          <div className="mt-12 text-center">
            <p className="text-muted-foreground mb-4">Also check our other Apple repair services:</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/macbook-service" className="text-primary hover:underline font-medium">
                MacBook Repair
              </Link>
              <span className="text-border">•</span>
              <Link to="/ipad-service" className="text-primary hover:underline font-medium">
                iPad Repair
              </Link>
              <span className="text-border">•</span>
              <Link to="/watch-service" className="text-primary hover:underline font-medium">
                Apple Watch Repair
              </Link>
              <span className="text-border">•</span>
              <Link to="/blog" className="text-primary hover:underline font-medium">
                All Repair Guides
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQSection
        title="iPhone Repair FAQs"
        subtitle="Common questions about our iPhone repair services in Patna"
        faqs={iphoneFaqs}
      />

      {/* CTA Section */}
      <section className="section-padding bg-foreground">
        <div className="container-apple text-center">
          <h2 className="text-3xl md:text-4xl font-semibold text-background mb-4">
            Need iPhone Repair?
          </h2>
          <p className="text-background/70 mb-8 max-w-xl mx-auto">
            Get your iPhone fixed by experts. Same day screen replacement and battery replacement available.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="apple-blue" size="lg" onClick={() => setIsBookingOpen(true)}>
              <Calendar className="w-5 h-5" />
              Book Appointment
            </Button>
            <Button variant="call" size="lg" asChild>
              <a href="tel:9798653591">
                <Phone className="w-5 h-5" />
                Call Now
              </a>
            </Button>
          </div>
        </div>
      </section>

      <BookingModal isOpen={isBookingOpen} onClose={() => setIsBookingOpen(false)} />
    </Layout>
  );
};

export default IPhoneService;
